import type { PropuestaDetail } from '@/types';
import { useUIStore } from '@/store/uiStore';
import {
  avance,
  avanceCompromisos,
  evTieneBloqueo,
  healthEv,
  hitosDeEv,
  hitosPorLente,
  iniciativaTieneBloqueo,
  tagsHeredados,
} from '@/lib/propuesta-selectors';
import styles from './facility-tabs.module.css';

interface Props {
  detail: PropuestaDetail;
  tagsById: Map<string, { nombre: string; color: string }>;
}

export function EntregasView({ detail, tagsById }: Props) {
  const lente = useUIStore((s) => s.lente);
  const scope = useUIStore((s) => s.scope);
  const setScope = useUIStore((s) => s.setScope);
  const clearScope = useUIStore((s) => s.clearScope);

  const evs = detail.entregasValor;
  if (evs.length === 0) {
    return (
      <div className="placeholder-wrap">
        <div className="icon">🎯</div>
        <h3>Sin Entregas de Valor</h3>
        <p>Esta propuesta no tiene EVs definidas.</p>
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      {evs.map((ev) => {
        const inisDeEv = detail.iniciativas.filter((i) => i.entregaValorId === ev.id);
        const compsDeEv = detail.compromisos.filter((c) =>
          inisDeEv.some((i) => i.id === c.iniciativaId),
        );
        const hitos = hitosPorLente(hitosDeEv(detail, ev.id), lente);
        const av = avance(hitos);
        const avComp = avanceCompromisos(detail, compsDeEv.map((c) => c.id));
        const evTags = tagsHeredados(detail, 'ev', ev.id);
        const scoped = scope.type === 'ev' && scope.id === ev.id;
        const hasBloqueo = evTieneBloqueo(detail, ev.id);
        const health = healthEv(detail, ev.id);
        const healthCls =
          health === 'red'
            ? styles.cardBloqueado
            : health === 'yellow'
              ? styles.cardHealthYellow
              : health === 'green'
                ? styles.cardHealthGreen
                : '';

        return (
          <div
            key={ev.id}
            className={`${styles.evCard} ${scoped ? styles.scoped : ''} ${healthCls}`}
            style={{ borderLeft: `4px solid ${ev.color}`, cursor: 'pointer' }}
            onClick={() => (scoped ? clearScope() : setScope({ type: 'ev', id: ev.id }))}
          >
            <div className={styles.evHead}>
              <div className={styles.evTitleRow}>
                <span className={styles.evBadge} style={{ background: ev.color }}>
                  {ev.codigo}
                </span>
                <span className={styles.evTitle}>{ev.nombre}</span>
              </div>
            </div>
            {ev.slogan && (
              <div className={styles.evDesc} style={{ fontStyle: 'italic' }}>
                "{ev.slogan}"
              </div>
            )}
            {ev.descripcion && <div className={styles.evDesc}>{ev.descripcion}</div>}
            {/* Barra 1 — Hitos cumplidos */}
            <div className={styles.progressBlock}>
              <div className={styles.progressLbl}>
                <span>Hitos</span>
                <span>
                  {av.done}/{av.total} · {av.pct}%
                </span>
              </div>
              <div className={styles.evBar}>
                <div
                  className={styles.evFill}
                  style={{ width: `${av.pct}%`, background: ev.color }}
                />
              </div>
            </div>

            {/* Barra 2 — Compromisos completados */}
            <div className={styles.progressBlock}>
              <div className={styles.progressLbl}>
                <span>Compromisos</span>
                <span>
                  {avComp.done}/{avComp.total} · {avComp.pct}%
                </span>
              </div>
              <div className={styles.evBar}>
                <div
                  className={styles.evFill}
                  style={{
                    width: `${avComp.pct}%`,
                    background: ev.color,
                    opacity: 0.55,
                  }}
                />
              </div>
            </div>

            <div className={styles.evStats}>
              <span className={styles.pill}>
                Iniciativas <strong>{inisDeEv.length}</strong>
              </span>
            </div>
            {evTags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                {evTags.map((tid) => {
                  const t = tagsById.get(tid);
                  if (!t) return null;
                  return (
                    <span
                      key={tid}
                      style={{
                        background: t.color + '22',
                        color: t.color,
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                      }}
                    >
                      {t.nombre}
                    </span>
                  );
                })}
              </div>
            )}
            <div className={styles.evInis}>
              {inisDeEv.map((i) => {
                const iniBloqueada = iniciativaTieneBloqueo(detail, i.id);
                return (
                  <span
                    key={i.id}
                    className={`${styles.evIniPill} ${iniBloqueada ? styles.evIniPillBloqueado : ''}`}
                    title={iniBloqueada ? 'Iniciativa con hito bloqueado' : undefined}
                  >
                    <span
                      className={styles.dot}
                      style={{ background: iniBloqueada ? '#dc2626' : ev.color }}
                    />
                    {i.codigo} · {i.nombre}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
